import { useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { CHART_VARS } from '../../components/chartVars';
import { BarChart } from '../../components/BarChart';
import { DataTable } from '../../components/DataTable';
import { DonutChart } from '../../components/DonutChart';
import { Panel } from '../../components/Panel';
import { SrbCode } from '../../components/SrbCode';
import { StatusChip } from '../../components/StatusChip';
import type { Tone } from '../../components/tone';
import { jod, percent } from '../../lib/currency';
import { kpiTarget } from '../../lib/kpi';
import { shortDate } from '../../lib/dates';
import { freightMode, monthly, STATUS_TONE, type Fin } from './calc';

/** The profit of the invoiced shipments, month by month this year */
export function ProfitChartPanel({ fins, title = 'Profit by Month' }: { fins: Fin[]; title?: string }) {
  const m = monthly(fins);
  return (
    <Panel title={title}>
      <BarChart title={title} categories={m.labels} series={[{ name: 'Profit', values: m.profit }]} format={jod} />
    </Panel>
  );
}

/** Where the profit comes from: sea (full container, less than container), air, road */
export function ProfitByModePanel({ fins }: { fins: Fin[] }) {
  const sold = fins.filter((f) => f.r.won ?? f.invoiced);
  const [show, setShow] = useState<'Percentage' | 'Amount'>('Percentage');
  const modes = ['Sea - FCL', 'Sea - LCL', 'Air', 'Road'];
  const slices = modes.map((name, i) => ({
    label: name,
    color: `var(--series-${i + 1})`,
    value: Math.max(
      0,
      Math.round(sold.filter((f) => freightMode(f) === name).reduce((n, f) => n + (f.profit ?? 0), 0)),
    ),
  }));
  return (
    <Panel title="Profit by Freight Mode">
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={show}
        onChange={(_e, v: 'Percentage' | 'Amount' | null) => v && setShow(v)}
        sx={{ alignSelf: 'flex-end' }}
      >
        {(['Percentage', 'Amount'] as const).map((v) => (
          <ToggleButton key={v} value={v} sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>
            {v}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Box sx={CHART_VARS}>
        <DonutChart
          centerLabel="Total Profit"
          centerValue={jod(slices.reduce((n, x) => n + x.value, 0))}
          slices={slices}
          valueText={(s, p) => (show === 'Percentage' ? `${p}%` : jod(s.value))}
        />
      </Box>
    </Panel>
  );
}

/** What needs you today: overdue invoices, unchecked agent invoices, missing costs, thin margins, JCS waiting, shipments not yet invoiced */
export function NeedsAttentionPanel({ fins }: { fins: Fin[] }) {
  const sum = (xs: Fin[], get: (f: Fin) => number) => xs.reduce((n, f) => n + get(f), 0);
  const overdue = fins.filter((f) => f.overdue);
  // an agent's invoice can only wait for checking once it is recorded (the sample data always has one)
  const unchecked = fins.filter(
    (f) => f.r.agent !== null && !f.r.agentPaid && !f.r.agentVerified && (f.r.agentInvoiced ?? true),
  );
  const missing = fins.filter((f) => f.costs === null);
  const thin = fins.filter((f) => f.margin !== null && f.margin < kpiTarget('lowMargin'));
  const jcs = fins.filter((f) => f.r.jcs === 'Awaiting approval');
  const notInvoiced = fins.filter(
    (f) => (f.ship?.stage === 'Arrived' || f.ship?.stage === 'Completed') && f.r.status === 'Pending Invoice',
  );
  const rows: { tone: Tone; count: number; text: string; amount?: number }[] = [
    { tone: 'error', count: overdue.length, text: 'Customer invoices overdue', amount: sum(overdue, (f) => f.owed) },
    {
      tone: 'warning',
      count: unchecked.length,
      text: 'Agent invoices pending verification',
      amount: sum(unchecked, (f) => f.agentOwed),
    },
    { tone: 'warning', count: missing.length, text: 'Shipments with missing costs' },
    { tone: 'info', count: thin.length, text: `Low margin shipments (<${kpiTarget('lowMargin')}%)` },
    { tone: 'info', count: jcs.length, text: 'JCS documents awaiting approval' },
    {
      tone: 'info',
      count: notInvoiced.length,
      text: 'Completed shipment not yet invoiced',
      amount: sum(notInvoiced, (f) => f.revenue),
    },
  ];
  return (
    <Panel title="Needs Attention">
      <Stack spacing={1.25}>
        {rows.map((r) => (
          <Stack key={r.text} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            <StatusChip label={String(r.count)} tone={r.count ? r.tone : 'neutral'} />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {r.text}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {r.amount ? jod(r.amount) : '–'}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}

/** Revenue, costs, profit and margin of each shipment */
export function ShipmentSummaryPanel({
  fins,
  title = 'Recent Shipments (Financial Summary)',
  limit,
}: {
  fins: Fin[];
  title?: string;
  limit?: number;
}) {
  const rows = [...fins].sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit ?? fins.length);
  return (
    <Panel title={title}>
      <DataTable<Fin>
        rows={rows}
        rowKey={(f) => f.r.srb}
        empty="No shipments yet."
        columns={[
          { header: 'Shipment', primary: true, nowrap: true, render: (f) => <SrbCode code={f.r.srb} /> },
          { header: 'Customer', render: (f) => f.ship?.customer ?? '–' },
          { header: 'Revenue', nowrap: true, render: (f) => jod(f.revenue) },
          { header: 'Costs', nowrap: true, render: (f) => (f.costs === null ? 'Missing' : jod(f.costs)) },
          { header: 'Profit', nowrap: true, render: (f) => (f.profit === null ? '–' : jod(f.profit)) },
          { header: 'Margin', nowrap: true, render: (f) => (f.margin === null ? '–' : percent(f.margin)) },
          {
            header: 'Status',
            nowrap: true,
            action: true,
            render: (f) => <StatusChip label={f.r.status} tone={STATUS_TONE[f.r.status]} />,
          },
        ]}
      />
    </Panel>
  );
}

const dueStatus = (overdue: boolean) => ({
  label: overdue ? 'Overdue' : 'Pending',
  tone: (overdue ? 'error' : 'warning') as Tone,
});

/** Customer invoices not yet paid in full */
export function CustomersOwePanel({ fins }: { fins: Fin[] }) {
  const rows = fins.filter((f) => f.owed > 0).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  return (
    <Panel title="Customers Owe SERB" subtitle={`${jod(rows.reduce((n, f) => n + f.owed, 0))} outstanding`}>
      <DataTable<Fin>
        rows={rows}
        rowKey={(f) => f.r.invoiceNo}
        empty="Nobody owes SERB anything."
        columns={[
          { header: 'Customer', primary: true, render: (f) => f.ship?.customer ?? '–' },
          { header: 'Invoice No.', nowrap: true, render: (f) => f.r.invoiceNo },
          { header: 'Amount', nowrap: true, render: (f) => jod(f.owed) },
          { header: 'Due Date', nowrap: true, render: (f) => shortDate(f.dueDate) },
          {
            header: 'Status',
            nowrap: true,
            action: true,
            render: (f) => <StatusChip label={dueStatus(f.overdue).label} tone={dueStatus(f.overdue).tone} />,
          },
        ]}
      />
    </Panel>
  );
}

/** Agent invoices not yet paid */
export function SerbOwesPanel({ fins }: { fins: Fin[] }) {
  const rows = fins.filter((f) => f.agentOwed > 0).sort((a, b) => a.agentDue.localeCompare(b.agentDue));
  return (
    <Panel
      title="SERB Owes Agents / Suppliers"
      subtitle={`${jod(rows.reduce((n, f) => n + f.agentOwed, 0))} outstanding`}
    >
      <DataTable<Fin>
        rows={rows}
        rowKey={(f) => f.r.agentInvoiceNo}
        empty="SERB owes nobody anything."
        columns={[
          { header: 'Agent / Supplier', primary: true, render: (f) => f.ship?.agent ?? '–' },
          { header: 'Invoice No.', nowrap: true, render: (f) => f.r.agentInvoiceNo },
          { header: 'Amount', nowrap: true, render: (f) => jod(f.agentOwed) },
          { header: 'Due Date', nowrap: true, render: (f) => shortDate(f.agentDue) },
          {
            header: 'Status',
            nowrap: true,
            action: true,
            render: (f) => <StatusChip label={dueStatus(f.agentOverdue).label} tone={dueStatus(f.agentOverdue).tone} />,
          },
        ]}
      />
    </Panel>
  );
}
